import { useState } from "react";
import { useOnboardingDraft } from "../OnboardingContext";
import styles from "../OnboardingWizard.module.css";
import type { StepProps } from "../OnboardingWizard";
import { SUPPORT_EMAIL } from "../../../config/links";

type GuideStep = {
  number: string;
  title: string;
  body: JSX.Element;
  ctaLabel: string;
  href: string;
};

// Steps 1-3 send the customer out to OpenRouter to create a key. Step 4
// (paste the key) is rendered separately below because it holds the input.
const OPENROUTER_GUIDE: ReadonlyArray<GuideStep> = [
  {
    number: "1",
    title: "Create your OpenRouter account",
    body: <>Sign up at OpenRouter; it takes under a minute.</>,
    ctaLabel: "Sign up at openrouter.ai",
    href: "https://openrouter.ai",
  },
  {
    number: "2",
    title: "Add billing",
    body: <>Open <strong>Settings → Credits</strong> and add a payment method. Most users start with $10.</>,
    ctaLabel: "Open billing settings",
    href: "https://openrouter.ai/settings/credits",
  },
  {
    number: "3",
    title: "Create an API key",
    body: <>Open <strong>Settings → API Keys → Create Key</strong>. Set a spend limit on the key (we recommend a monthly one so your dashboard can track it), then copy it.</>,
    ctaLabel: "Open API keys page",
    href: "https://openrouter.ai/settings/keys",
  },
];

type Benefit = {
  title: string;
  desc: string;
  icon: JSX.Element;
};

const BENEFITS: ReadonlyArray<Benefit> = [
  {
    title: "You control the spend",
    desc: "Set a hard limit on your key, and your dashboard tracks usage in real time.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    title: "Hundreds of models",
    desc: "Claude, GPT, Gemini and more. Switch anytime, no new accounts.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 2l9 5-9 5-9-5z" />
        <path d="M3 12l9 5 9-5" />
        <path d="M3 17l9 5 9-5" />
      </svg>
    ),
  },
  {
    title: "Pay as you go",
    desc: "No subscription, no lock-in. You only pay for what your agent uses.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M13 2L4 14h6l-1 8 9-12h-6z" />
      </svg>
    ),
  },
];

const KEY_PATTERN = /^sk-or-v1-[a-zA-Z0-9-]{20,}$/;

type AiMethod = "openrouter" | "openai-subscription";

// No default and no recommendation - the customer picks. The comparison
// lives on the cards themselves: short lines side by side beat a paragraph
// under the grid.
const PROVIDERS: ReadonlyArray<{ method: AiMethod; name: string; points: ReadonlyArray<string> }> = [
  {
    method: "openai-subscription",
    name: "OpenAI subscription",
    points: [
      "Fixed monthly billing through your ChatGPT subscription.",
      "Predictable and very stable. OpenAI models only.",
    ],
  },
  {
    method: "openrouter",
    name: "OpenRouter",
    points: [
      "Hundreds of models at different price points and strengths.",
      "Pay for what you use. Flexible, but not as cost-efficient.",
    ],
  },
];

export const AiSetupStep = ({ onNext, onBack, canBack }: StepProps): JSX.Element => {
  const { draft, update } = useOnboardingDraft();
  const [showKey, setShowKey] = useState<boolean>(false);

  const method: AiMethod | undefined = draft.aiSetupMethod;
  const trimmedKey = (draft.openrouterApiKey ?? "").trim();
  const keyLooksValid = trimmedKey.length === 0 ? null : KEY_PATTERN.test(trimmedKey);
  const canProceed =
    method === "openai-subscription"
      ? true
      : method === "openrouter"
        ? trimmedKey.length > 0 && keyLooksValid !== false
        : false;

  return (
    <section className={styles.card}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>How we set up your AI</h2>
        <p className={styles.stepHint}>
          Your agent needs an AI provider behind it. Pick how you want to power it; you can always
          switch later.
        </p>
      </div>

      <div className={styles.field}>
        <div className={`${styles.optionGrid} ${styles.optionGridUniform}`}>
          {PROVIDERS.map((p) => {
            const isSelected = method === p.method;
            return (
              <button
                key={p.method}
                type="button"
                className={`${styles.optionTile} ${isSelected ? styles.optionTileSelected : ""}`}
                onClick={() => update("aiSetupMethod", p.method)}
                aria-pressed={isSelected}
              >
                <span className={styles.optionTitle}>{p.name}</span>
                {p.points.map((point) => (
                  <span key={point} className={styles.optionMeta}>{point}</span>
                ))}
              </button>
            );
          })}
        </div>
      </div>

      {method === "openai-subscription" ? (
        <>
          <div className={styles.field}>
            <label className={styles.label}>Your ChatGPT plan</label>
            <span className={styles.hint}>
              Don't have a subscription yet? Plus (US$20/month) is a solid starting point for most
              agents; Pro (US$200/month) suits heavy daily use, and upgrading later is easy if you
              find yourself running out of usage.
            </span>
            <a
              href="https://openai.com/chatgpt/pricing/"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideStepLink}
            >
              See ChatGPT plans
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 3h8v8" />
                <path d="M13 3l-9 9" />
              </svg>
            </a>
          </div>
          <div className={styles.calloutBanner}>
            <strong>Already subscribed? You're set.</strong> There's nothing to paste here; we'll
            connect your subscription to your agent together on your onboarding call.
          </div>
        </>
      ) : null}

      {method === "openrouter" ? (
      <>
      <details className={styles.disclosure}>
        <summary className={styles.disclosureSummary}>Why OpenRouter &amp; how billing works</summary>
        <div className={styles.disclosureBody}>
          <div className={styles.benefitGrid}>
            {BENEFITS.map((b) => (
              <div key={b.title} className={styles.benefit}>
                <span className={styles.benefitIcon}>{b.icon}</span>
                <span className={styles.benefitTitle}>{b.title}</span>
                <span className={styles.benefitDesc}>{b.desc}</span>
              </div>
            ))}
          </div>
          <p>
            <strong className={styles.disclosureLead}>What it is.</strong> OpenRouter is a routing layer
            that sits in front of every major AI provider, so a single key gives your agent access to all
            of them without separate accounts for OpenAI, Anthropic, and Google.
          </p>
          <p>
            <strong className={styles.disclosureLead}>How billing works.</strong> You top up your own
            OpenRouter account and set a spend limit on the key. We never see your card, nothing can run a
            bill past the cap you set, and you only pay for what your agent actually uses.
          </p>
          <p>
            <strong className={styles.disclosureLead}>Want to use something else?</strong> We're not
            affiliated with OpenRouter in any way; we just find it the simplest, most flexible way to get
            started. Prefer OpenAI, Anthropic, or your own provider account? Email us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll set it up with you.
          </p>
        </div>
      </details>

      <div className={styles.field}>
        <span className={styles.guideKicker}>Get your key in 4 quick steps</span>
        <ol className={styles.guideSteps}>
          {OPENROUTER_GUIDE.map((s) => (
            <li key={s.number} className={styles.guideStep}>
              <div className={styles.guideStepNumber}>{s.number}</div>
              <div className={styles.guideStepBody}>
                <h3 className={styles.guideStepTitle}>{s.title}</h3>
                <p className={styles.guideStepDesc}>{s.body}</p>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.guideStepLink}
                >
                  {s.ctaLabel}
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M5 3h8v8" />
                    <path d="M13 3l-9 9" />
                  </svg>
                </a>
              </div>
            </li>
          ))}
          <li className={styles.guideStep}>
            <div className={styles.guideStepNumber}>4</div>
            <div className={styles.guideStepBody}>
              <h3 className={styles.guideStepTitle}>Paste your OpenRouter API key</h3>
              <p className={styles.guideStepDesc}>
                Copy the key from OpenRouter (it starts with <code>sk-or-v1-</code>) and paste it here.
              </p>
              <div className={styles.passwordField}>
                <input
                  id="onb-or-key"
                  className={styles.input}
                  type={showKey ? "text" : "password"}
                  value={draft.openrouterApiKey ?? ""}
                  onChange={(e) => update("openrouterApiKey", e.target.value || undefined)}
                  placeholder="sk-or-v1-..."
                  aria-label="OpenRouter API key"
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowKey((s) => !s)}
                  aria-label={showKey ? "Hide key" : "Show key"}
                  title={showKey ? "Hide key" : "Show key"}
                >
                  {showKey ? (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M2 9s2.5-5 7-5c1.4 0 2.7.5 3.8 1.2M16 9s-2.5 5-7 5c-1.4 0-2.7-.5-3.8-1.2" />
                      <path d="M2 2l14 14" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M1.5 9s3-5.5 7.5-5.5S16.5 9 16.5 9s-3 5.5-7.5 5.5S1.5 9 1.5 9z" />
                      <circle cx="9" cy="9" r="2.5" />
                    </svg>
                  )}
                </button>
              </div>
              {keyLooksValid === false && (
                <span className={styles.errorInline}>
                  Key should look like <code>sk-or-v1-…</code>. Double-check you copied the whole thing.
                </span>
              )}
              {keyLooksValid !== false && (
                <span className={styles.hint}>
                  Stored as a SecureString: never echoed back, never logged, only used to power your agent.
                </span>
              )}
            </div>
          </li>
        </ol>
      </div>
      </>
      ) : null}

      <div className={styles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onBack} disabled={!canBack}>Back</button>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={onNext}
          disabled={!canProceed}
          title={
            method === undefined
              ? "Pick a provider to continue"
              : method === "openai-subscription"
                ? undefined
                : trimmedKey.length === 0
                  ? "Paste your OpenRouter API key to continue"
                  : keyLooksValid === false
                    ? "API key format doesn't look right"
                    : undefined
          }
        >
          Next
        </button>
      </div>
    </section>
  );
};
